import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

const ExportModal = ({ isOpen, onClose, onExport, totalRecords }) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'csv',
    dateRange: { start: '', end: '' },
    includeColumns: {
      timestamp: true,
      user: true,
      vin: true,
      vehicle: true,
      latency: true,
      status: true,
      location: true,
      sessionDetails: false,
      technicalDetails: false,
      complianceData: false
    },
    maxRecords: 10000
  });

  const [isExporting, setIsExporting] = useState(false);

  const formatOptions = [
    { value: 'csv', label: 'CSV (Valores separados por comas)' },
    { value: 'xlsx', label: 'Excel (XLSX)' },
    { value: 'json', label: 'JSON (Datos estructurados)' }
  ];

  const columnOptions = [
    { key: 'timestamp', label: 'Fecha y Hora', required: true },
    { key: 'user', label: 'Usuario', required: true },
    { key: 'vin', label: 'VIN', required: true },
    { key: 'vehicle', label: 'Información del Vehículo', required: false },
    { key: 'latency', label: 'Latencia', required: false },
    { key: 'status', label: 'Estado de Consulta', required: false },
    { key: 'location', label: 'Ubicación e IP', required: false },
    { key: 'sessionDetails', label: 'Detalles de Sesión', required: false },
    { key: 'technicalDetails', label: 'Información Técnica', required: false },
    { key: 'complianceData', label: 'Datos de Cumplimiento', required: false }
  ];

  const handleColumnToggle = (columnKey) => {
    setExportConfig(prev => ({
      ...prev,
      includeColumns: {
        ...prev?.includeColumns,
        [columnKey]: !prev?.includeColumns?.[columnKey]
      }
    }));
  };

  const handleDateRangeChange = (type, value) => {
    setExportConfig(prev => ({
      ...prev,
      dateRange: {
        ...prev?.dateRange,
        [type]: value
      }
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExport(exportConfig);
      onClose();
    } catch (error) {
      console.error('Error durante la exportación:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const getEstimatedFileSize = () => {
    const selectedColumns = Object.values(exportConfig?.includeColumns)?.filter(Boolean)?.length;
    const estimatedSizeKB = Math.round((exportConfig?.maxRecords * selectedColumns * 50) / 1024);
    
    if (estimatedSizeKB < 1024) {
      return `${estimatedSizeKB} KB`;
    } else {
      return `${(estimatedSizeKB / 1024)?.toFixed(1)} MB`;
    }
  };

  const selectedColumnsCount = Object.values(exportConfig?.includeColumns)?.filter(Boolean)?.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Exportar Datos de Auditoría</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Configurar y descargar registros de auditoría
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <Select
              label="Formato de archivo"
              description="Selecciona el formato para la exportación"
              options={formatOptions}
              value={exportConfig?.format}
              onChange={(value) => setExportConfig(prev => ({ ...prev, format: value }))}
            />
          </div>

          {/* Date Range */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Rango de fechas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="date"
                label="Fecha de inicio"
                value={exportConfig?.dateRange?.start}
                onChange={(e) => handleDateRangeChange('start', e?.target?.value)}
              />
              <Input
                type="date"
                label="Fecha de fin"
                value={exportConfig?.dateRange?.end}
                onChange={(e) => handleDateRangeChange('end', e?.target?.value)}
              />
            </div>
          </div>

          {/* Column Selection */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">
              Columnas a incluir ({selectedColumnsCount} seleccionadas)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {columnOptions?.map((column) => (
                <Checkbox
                  key={column?.key}
                  label={column?.label}
                  checked={exportConfig?.includeColumns?.[column?.key]}
                  onChange={() => handleColumnToggle(column?.key)}
                  disabled={column?.required}
                  description={column?.required ? 'Campo obligatorio' : undefined}
                />
              ))}
            </div>
          </div>

          {/* Export Limits */}
          <div>
            <Input
              type="number"
              label="Límite máximo de registros"
              description="Máximo 50,000 registros por exportación"
              value={exportConfig?.maxRecords}
              onChange={(e) => setExportConfig(prev => ({ 
                ...prev, 
                maxRecords: Math.min(50000, Math.max(1, parseInt(e?.target?.value) || 1))
              }))}
              min="1"
              max="50000"
            />
          </div>

          {/* Export Summary */}
          <div className="bg-muted rounded-lg p-4">
            <h4 className="text-sm font-medium text-foreground mb-2">Resumen de exportación</h4>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex justify-between">
                <span>Registros totales disponibles:</span>
                <span className="font-medium">{totalRecords?.toLocaleString('es-MX')}</span>
              </div>
              <div className="flex justify-between">
                <span>Registros a exportar:</span>
                <span className="font-medium">
                  {Math.min(exportConfig?.maxRecords, totalRecords)?.toLocaleString('es-MX')}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Columnas incluidas:</span>
                <span className="font-medium">{selectedColumnsCount}</span>
              </div>
              <div className="flex justify-between">
                <span>Tamaño estimado:</span>
                <span className="font-medium">{getEstimatedFileSize()}</span>
              </div>
            </div>
          </div>

          {/* Warning for large exports */}
          {exportConfig?.maxRecords > 10000 && (
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <Icon name="AlertTriangle" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Exportación grande detectada</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Esta exportación puede tardar varios minutos en completarse. 
                  Se recomienda usar filtros de fecha para reducir el tamaño.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            Los datos se exportarán con codificación UTF-8
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isExporting}
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              onClick={handleExport}
              loading={isExporting}
              iconName="Download"
              iconPosition="left"
              iconSize={16}
              disabled={selectedColumnsCount === 0}
            >
              {isExporting ? 'Exportando...' : 'Exportar datos'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;